import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, Crown, Loader2, ArrowRight, Shield, Beaker, Package, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const [planData, setPlanData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  useEffect(() => { loadPlan(); }, []);

  const loadPlan = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const res = await fetch(`${API_BASE}/api/user/plan?user_id=${user.id || 'demo-user'}`);
      const data = await res.json();
      setPlanData(data);
    } catch (e) { toast.error('Erreur'); }
    finally { setLoading(false); }
  };

  const handleUpgrade = async (billing_cycle = 'monthly') => {
    setCheckingOut(billing_cycle);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const res = await fetch(`${API_BASE}/api/stripe/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ origin_url: window.location.origin, user_id: user.id, billing_cycle })
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error('Erreur Stripe');
      }
    } catch (e) { toast.error('Erreur de paiement'); }
    finally { setCheckingOut(false); }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const res = await fetch(`${API_BASE}/api/subscription/cancel`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id })
      });
      if (res.ok) {
        toast.success('Désabonnement programmé');
        setShowCancelConfirm(false);
        loadPlan();
      } else { toast.error('Erreur'); }
    } catch (e) { toast.error('Erreur'); }
    finally { setCancelling(false); }
  };

  const handleReactivate = async () => {
    setCancelling(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const res = await fetch(`${API_BASE}/api/subscription/reactivate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id })
      });
      if (res.ok) {
        user.plan = 'premium';
        localStorage.setItem('user', JSON.stringify(user));
        toast.success('Désabonnement annulé — vous restez Premium');
        setShowCancelConfirm(false);
        loadPlan();
      } else { toast.error('Erreur'); }
    } catch (e) { toast.error('Erreur'); }
    finally { setCancelling(false); }
  };

  if (loading) return <div className="p-8"><div className="skeleton-shimmer h-64 rounded-lg"></div></div>;

  const isPremium = planData?.plan === 'premium';
  const isAnnual = planData?.billing_cycle === 'annual';

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <h1 className="text-4xl font-semibold mb-2 tracking-tight">Mon Abonnement</h1>
      <p className="text-muted-foreground mb-8">Gérez votre plan et vos limites d'utilisation</p>

      {/* Current plan */}
      <Card className="mb-8">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isPremium ? <Crown className="text-amber-500" size={24} /> : <Shield className="text-[#205F79]" size={24} />}
              <div>
                <CardTitle className="text-xl">
                  Plan {isPremium ? 'Premium' : 'Gratuit'}
                  {isPremium && isAnnual && <span className="ml-2 text-sm font-medium text-[#205F79]">— Annuel</span>}
                  {isPremium && !isAnnual && <span className="ml-2 text-sm font-medium text-muted-foreground">— Mensuel</span>}
                </CardTitle>
                <p className="text-sm text-muted-foreground">{isPremium ? 'Accès illimité' : 'Limité à 3 produits et 10 matières'}</p>
              </div>
            </div>
            {isPremium && (
              <span className="px-3 py-1 bg-amber-100 text-amber-800 text-xs font-semibold rounded-full">ACTIF</span>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 rounded-lg bg-muted">
              <div className="flex items-center gap-2 mb-1">
                <Beaker size={16} className="text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Produits finis</p>
              </div>
              <p className="text-2xl font-bold">
                {planData?.usage?.finished_products || 0}
                <span className="text-base font-normal text-muted-foreground">
                  {isPremium ? '' : ` / ${planData?.limits?.finished_products || 3}`}
                </span>
              </p>
            </div>
            <div className="p-4 rounded-lg bg-muted">
              <div className="flex items-center gap-2 mb-1">
                <Package size={16} className="text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Matières premières</p>
              </div>
              <p className="text-2xl font-bold">
                {planData?.usage?.raw_materials || 0}
                <span className="text-base font-normal text-muted-foreground">
                  {isPremium ? '' : ` / ${planData?.limits?.raw_materials || 10}`}
                </span>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upgrade CTA */}
      {!isPremium && (
        <Card className="border-[#205F79] border-2 overflow-hidden">
          <div className="bg-[#205F79] px-6 py-3 flex items-center gap-2">
            <Crown className="text-white" size={18} />
            <span className="text-white font-semibold text-sm">Passer au Premium</span>
          </div>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Monthly */}
              <div className="p-5 rounded-lg border-2 border-gray-200 bg-white" data-testid="plan-card-monthly">
                <p className="text-sm font-semibold text-gray-600 uppercase tracking-wider mb-2">Mensuel</p>
                <p className="text-3xl font-bold text-[#205F79]">25€<span className="text-base font-normal text-muted-foreground">/mois</span></p>
                <p className="text-xs text-muted-foreground mt-1">Sans engagement, annulable à tout moment</p>
                <Button className="w-full mt-4 bg-[#205F79] hover:bg-[#184a5e] gap-2" onClick={() => handleUpgrade('monthly')} disabled={!!checkingOut} data-testid="btn-upgrade-monthly">
                  {checkingOut === 'monthly' ? <Loader2 className="animate-spin" size={16} /> : <Crown size={16} />}
                  {checkingOut === 'monthly' ? 'Redirection...' : 'S\'abonner — 25€/mois'}
                </Button>
              </div>
              {/* Annual */}
              <div className="relative p-5 rounded-lg border-2 border-amber-400 bg-amber-50" data-testid="plan-card-annual">
                <span className="absolute -top-3 right-4 px-2 py-0.5 bg-amber-500 text-white text-[10px] font-bold rounded-full uppercase tracking-wide">Économisez 50€</span>
                <p className="text-sm font-semibold text-amber-700 uppercase tracking-wider mb-2">Annuel</p>
                <p className="text-3xl font-bold text-[#205F79]">250€<span className="text-base font-normal text-muted-foreground">/an</span></p>
                <p className="text-xs text-amber-700 mt-1 font-medium">Soit 20,83€/mois — 2 mois offerts</p>
                <Button className="w-full mt-4 bg-amber-500 hover:bg-amber-600 text-white gap-2" onClick={() => handleUpgrade('annual')} disabled={!!checkingOut} data-testid="btn-upgrade-annual">
                  {checkingOut === 'annual' ? <Loader2 className="animate-spin" size={16} /> : <Crown size={16} />}
                  {checkingOut === 'annual' ? 'Redirection...' : 'S\'abonner — 250€/an'}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              {['Produits illimités', 'Matières illimitées', 'DIP PDF conformes', 'Assistant IA complet', 'Export PDF & CSV', 'Support prioritaire'].map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <Check size={14} className="text-green-500 shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground text-center">Paiement sécurisé via Stripe. L'abonnement annuel est une souscription unique sans remboursement, valable 1 an.</p>
          </CardContent>
        </Card>
      )}

      {/* Cancel / Manage subscription for Premium users */}
      {isPremium && isAnnual && (
        <Card className="border-amber-200 bg-amber-50/40">
          <CardContent className="pt-6">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="font-medium text-amber-800 mb-1">Abonnement annuel actif</p>
              <p className="text-sm text-amber-700">
                Votre abonnement Premium annuel est valable jusqu'au{' '}
                <strong>{planData.subscription_ends_at ? new Date(planData.subscription_ends_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}</strong>.
                Il s'agit d'une souscription unique sans remboursement. À l'échéance, votre plan repassera automatiquement en Gratuit, sauf renouvellement.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {isPremium && !isAnnual && (
        <Card className="border-gray-200">
          <CardContent className="pt-6">
            {planData?.cancel_requested ? (
              <div className="space-y-4">
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="font-medium text-amber-800 mb-1">Désabonnement programmé</p>
                  <p className="text-sm text-amber-700">
                    Votre plan Premium reste actif jusqu'au{' '}
                    <strong>{planData.subscription_ends_at ? new Date(planData.subscription_ends_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}</strong>.
                    Après cette date, votre plan passera en Gratuit.
                  </p>
                </div>
                <Button className="gap-2 bg-[#205F79] hover:bg-[#184a5e]" onClick={handleReactivate} disabled={cancelling} data-testid="btn-reactivate">
                  {cancelling ? <Loader2 className="animate-spin" size={16} /> : <Crown size={16} />}
                  Annuler le désabonnement — rester Premium
                </Button>
              </div>
            ) : !showCancelConfirm ? (
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">Gérer mon abonnement</p>
                  <p className="text-sm text-muted-foreground">Vous pouvez annuler votre abonnement à tout moment.</p>
                </div>
                <Button variant="outline" className="text-destructive border-destructive/30 hover:bg-destructive/5" 
                  onClick={() => setShowCancelConfirm(true)} data-testid="btn-show-cancel">
                  Se désabonner
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="font-medium text-amber-800 mb-1">Êtes-vous sûr ?</p>
                  <p className="text-sm text-amber-700">Votre plan Premium restera actif jusqu'à la fin de votre période en cours (30 jours après le dernier paiement). Vous pourrez annuler cette décision à tout moment avant la date d'échéance.</p>
                </div>
                <div className="flex gap-3">
                  <Button variant="destructive" className="gap-2" onClick={handleCancel} disabled={cancelling} data-testid="btn-confirm-cancel">
                    {cancelling ? <Loader2 className="animate-spin" size={16} /> : <XCircle size={16} />}
                    Confirmer le désabonnement
                  </Button>
                  <Button variant="outline" onClick={() => setShowCancelConfirm(false)} data-testid="btn-cancel-cancel">
                    Annuler
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SubscriptionPage;
